import type { OnboardingStep, OnboardingStepId, OnboardingStepStatus } from './onboarding.types';

const I18N_BASE = 'recruitment.onboarding_checklist.steps';

/** Where each step returns to after a successful save. Passed as a `returnUrl`
 *  query param and bound to the destination's `returnUrl` input, so completing a
 *  step from the checklist sends the user straight back to the dashboard. */
const DASHBOARD = '/recruitment/dashboard';

/** Static definition of every step, in display order. Completion is layered on
 *  per render by {@link buildSteps}; this table holds only the fixed metadata. */
const STEP_DEFS: ReadonlyArray<Omit<OnboardingStep, 'complete'>> = [
  {
    id: 'account-manager',
    titleKey: `${I18N_BASE}.account-manager.title`,
    descriptionKey: `${I18N_BASE}.account-manager.description`,
    icon: { fontSet: 'uicon-rr', fontIcon: 'fi-rr-sparkles' },
    route: '/settings/activate-agent',
    queryParams: { returnUrl: DASHBOARD },
  },
  {
    id: 'first-hire-request',
    titleKey: `${I18N_BASE}.first-hire-request.title`,
    descriptionKey: `${I18N_BASE}.first-hire-request.description`,
    icon: { fontSet: 'uicon-rr', fontIcon: 'fi-rr-megaphone' },
    // No returnUrl: unlike the other steps, hire request creation has its own
    // destination (the new hire request's details), so it should land there
    // even when the rest of company setup isn't complete yet.
    route: '/recruitment/hire-requests/new',
  },
  {
    id: 'company-profile',
    titleKey: `${I18N_BASE}.company-profile.title`,
    descriptionKey: `${I18N_BASE}.company-profile.description`,
    icon: { fontSet: 'uicon-rr', fontIcon: 'fi-rr-building' },
    route: '/settings/company-profile',
    // Land directly in the profile edit form rather than the read-only view.
    queryParams: { edit: '1', returnUrl: DASHBOARD },
  },
];

/** Combine the static step table with the supplied completion flags. */
export function buildSteps(flags: Record<OnboardingStepId, boolean>): OnboardingStep[] {
  return STEP_DEFS.map((def) => ({ ...def, complete: flags[def.id] }));
}

/** Whole-number percentage of completed steps. */
export function percentOf(steps: readonly OnboardingStep[]): number {
  if (steps.length === 0) return 0;
  const done = steps.filter((s) => s.complete).length;
  return Math.round((done / steps.length) * 100);
}

/** The first incomplete step's id, or `null` when every step is complete. */
export function currentStepIdOf(steps: readonly OnboardingStep[]): OnboardingStepId | null {
  return steps.find((s) => !s.complete)?.id ?? null;
}

/** Visual state of a step: completed → `done`, first incomplete → `current`, rest → `pending`. */
export function statusOf(steps: readonly OnboardingStep[], step: OnboardingStep): OnboardingStepStatus {
  if (step.complete) return 'done';
  return step.id === currentStepIdOf(steps) ? 'current' : 'pending';
}
